import { GIFEncoder, applyPalette, quantize } from 'gifenc'

// a punk's on-chain image is a 24x24 svg holding two pixel-art png layers: the punk itself, and its
// radioactive glow on top, whose opacity the svg's css animates. we composite those layers ourselves
// rather than drawing the svg to a canvas, because svgs with <foreignObject> taint the canvas.

export const PUNK_PIXELS = 24
export const EXPORT_SIZE = 720
const SCALE = EXPORT_SIZE / PUNK_PIXELS

// 25fps; 40ms divides the 4s glow loop evenly and is a whole number of the gif's 10ms ticks
export const GIF_FRAME_MS = 40

// the renderer's glow css is `animation:g 2s ease-in-out infinite alternate` + `@keyframes g{to{opacity:.4}}`
// read it from the svg anyway so exports keep matching if that ever changes
export function parseGlowAnimation (svg) {
  const duration = svg.match(/animation:\s*g\s+([\d.]+)(m?s)/)
  const opacity = svg.match(/@keyframes\s+g\s*\{\s*to\s*\{\s*opacity:\s*([\d.]+)/)
  return {
    halfCycleMs: duration ? parseFloat(duration[1]) * (duration[2] === 's' ? 1000 : 1) : 2000,
    minOpacity: opacity ? parseFloat(opacity[1]) : 0.4
  }
}

// css `ease-in-out`, i.e. cubic-bezier(0.42, 0, 0.58, 1)
export function easeInOut (x) {
  const bezier = (s, p1, p2) => 3 * (1 - s) * (1 - s) * s * p1 + 3 * (1 - s) * s * s * p2 + s * s * s
  let lo = 0
  let hi = 1
  for (let i = 0; i < 30; i++) {
    const mid = (lo + hi) / 2
    if (bezier(mid, 0.42, 0.58) < x) { lo = mid } else { hi = mid }
  }
  return bezier((lo + hi) / 2, 0, 1)
}

// glow opacity `ms` into the loop. `alternate` plays the half cycle forwards then backwards,
// so one seamless loop is two half cycles
export function glowOpacityAt (ms, { halfCycleMs, minOpacity }) {
  const loopMs = halfCycleMs * 2
  const t = ((ms % loopMs) + loopMs) % loopMs
  const progress = t < halfCycleMs ? t / halfCycleMs : (loopMs - t) / halfCycleMs
  return 1 - (1 - minOpacity) * easeInOut(progress)
}

function decodeDataUrl (url) {
  const [header, data] = url.split(',', 2)
  return header.endsWith(';base64') ? atob(data) : decodeURIComponent(data)
}

function readPixels (src) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = canvas.height = PUNK_PIXELS
      const ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0, PUNK_PIXELS, PUNK_PIXELS)
      resolve(ctx.getImageData(0, 0, PUNK_PIXELS, PUNK_PIXELS).data)
    }
    img.onerror = () => reject(new Error('Could not decode punk image layer'))
    img.src = src
  })
}

export async function loadLayers (imageDataUrl) {
  const svg = decodeDataUrl(imageDataUrl)
  const doc = new DOMParser().parseFromString(svg, 'image/svg+xml')
  const layers = [...doc.getElementsByTagName('foreignObject')]
  const glow = layers.find(layer => layer.classList.contains('g'))
  const base = layers.find(layer => !layer.classList.contains('g'))
  if (!base || !glow) {
    throw new Error('Unexpected punk image format')
  }
  const layerSrc = layer => layer.getElementsByTagNameNS('*', 'img')[0].getAttribute('src')
  const [basePixels, glowPixels] = await Promise.all([readPixels(layerSrc(base)), readPixels(layerSrc(glow))])
  return { basePixels, glowPixels, animation: parseGlowAnimation(svg) }
}

// the 24x24 frame with the glow layer at `opacity`
export function composite ({ basePixels, glowPixels }, opacity) {
  const out = new Uint8ClampedArray(basePixels.length)
  for (let i = 0; i < out.length; i += 4) {
    const a = (glowPixels[i + 3] / 255) * opacity
    out[i] = basePixels[i] * (1 - a) + glowPixels[i] * a
    out[i + 1] = basePixels[i + 1] * (1 - a) + glowPixels[i + 1] * a
    out[i + 2] = basePixels[i + 2] * (1 - a) + glowPixels[i + 2] * a
    out[i + 3] = 255
  }
  return out
}

export function sameFrame (a, b) {
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) { return false }
  }
  return true
}

// nearest-neighbor upscale of a 24x24 palette-indexed frame
function upscaleIndices (indices) {
  const out = new Uint8Array(EXPORT_SIZE * EXPORT_SIZE)
  for (let y = 0; y < PUNK_PIXELS; y++) {
    const row = new Uint8Array(EXPORT_SIZE)
    for (let x = 0; x < EXPORT_SIZE; x++) {
      row[x] = indices[y * PUNK_PIXELS + Math.floor(x / SCALE)]
    }
    for (let dy = 0; dy < SCALE; dy++) {
      out.set(row, (y * SCALE + dy) * EXPORT_SIZE)
    }
  }
  return out
}

// exact palette when the loop uses <= 256 colors (it normally does), quantized otherwise
export function buildPalette (frames) {
  const colors = new Map()
  for (const frame of frames) {
    for (let i = 0; i < frame.length; i += 4) {
      const key = (frame[i] << 16) | (frame[i + 1] << 8) | frame[i + 2]
      if (!colors.has(key)) { colors.set(key, colors.size) }
    }
  }
  if (colors.size <= 256) {
    const palette = [...colors.keys()].map(c => [c >> 16, (c >> 8) & 255, c & 255])
    const index = frame => {
      const out = new Uint8Array(frame.length / 4)
      for (let i = 0; i < out.length; i++) {
        out[i] = colors.get((frame[i * 4] << 16) | (frame[i * 4 + 1] << 8) | frame[i * 4 + 2])
      }
      return out
    }
    return { palette, index }
  }

  const all = new Uint8Array(frames.length * frames[0].length)
  frames.forEach((frame, i) => all.set(frame, i * frame.length))
  const palette = quantize(all, 256)
  return { palette, index: frame => applyPalette(new Uint8Array(frame), palette) }
}

// a still 720x720 png of the punk at full glow
export async function renderPunkPNG (imageDataUrl) {
  const layers = await loadLayers(imageDataUrl)
  const small = document.createElement('canvas')
  small.width = small.height = PUNK_PIXELS
  small.getContext('2d').putImageData(new ImageData(composite(layers, 1), PUNK_PIXELS, PUNK_PIXELS), 0, 0)

  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = EXPORT_SIZE
  const ctx = canvas.getContext('2d')
  ctx.imageSmoothingEnabled = false
  ctx.drawImage(small, 0, 0, EXPORT_SIZE, EXPORT_SIZE)
  return new Promise(resolve => canvas.toBlob(resolve, 'image/png'))
}

// a 720x720 gif of exactly one glow cycle, which loops seamlessly forever
export async function renderPunkGIF (imageDataUrl) {
  const layers = await loadLayers(imageDataUrl)
  const loopMs = layers.animation.halfCycleMs * 2
  const frameCount = Math.round(loopMs / GIF_FRAME_MS)

  // merge runs of identical frames (the glow barely moves at the ends of its ease) into longer ones
  const frames = []
  for (let i = 0; i < frameCount; i++) {
    const pixels = composite(layers, glowOpacityAt(i * GIF_FRAME_MS, layers.animation))
    const last = frames[frames.length - 1]
    if (last && sameFrame(last.pixels, pixels)) {
      last.delay += GIF_FRAME_MS
    } else {
      frames.push({ pixels, delay: GIF_FRAME_MS })
    }
  }

  const { palette, index } = buildPalette(frames.map(frame => frame.pixels))
  const gif = GIFEncoder()
  for (let i = 0; i < frames.length; i++) {
    gif.writeFrame(upscaleIndices(index(frames[i].pixels)), EXPORT_SIZE, EXPORT_SIZE, {
      delay: frames[i].delay,
      ...(i === 0 ? { palette, repeat: 0 } : {})
    })
    // let the page breathe between frames
    await new Promise(resolve => setTimeout(resolve))
  }
  gif.finish()
  return new Blob([gif.bytes()], { type: 'image/gif' })
}

export function downloadBlob (blob, filename) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
